import { create } from 'zustand';
import { 
  Api, 
  IShipmentStatus, 
} from '../api/api';

// Интерфейс для размера в отгрузке
export interface ShipmentSize {
  id: number;
  size: string;
  price: string;
  purchasePrice?: number;
  status?: string;
  shipmentDate?: string | null;
  inShipmentDays?: number;
  avgMarketPrice?: string;
  stockXPrice?: string;
  goatPrice?: string;
  poizonPrice?: string;
}

// Интерфейс для сгруппированного товара в отгрузке
export interface GroupedShipmentItem {
  article: string;
  brand: string;
  model: string;
  color: string;
  imageUrl: string;
  sizes: ShipmentSize[];
  priceRange: {
    min: number;
    max: number;
  };
}

interface ShipmentsState {
  // Данные
  shipmentStatuses: IShipmentStatus[];
  shipmentsItems: GroupedShipmentItem[];
  
  // Состояния загрузки
  isLoading: boolean;
  isInitialLoading: boolean;
  isLoadingStatuses: boolean;
  isLoadingArticles: boolean;
  isLoadingModels: boolean;
  
  // Поиск
  searchQuery: string;
  
  // Пагинация
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  
  // Фильтры
  selectedArticleId: string | null;
  selectedModelId: string | null;
  priceFrom: number | null;
  priceTo: number | null;
  daysOnShipment: number | null;
  selectedShipmentStatusId: number | null;
  
  // Доступные значения для фильтров
  availableArticles: string[];
  availableModels: string[];
  
  // Действия
  fetchShipmentStatuses: () => Promise<void>;
  fetchShipmentsItems: (params?: {
    page?: number;
    limit?: number;
  }) => Promise<void>;
  
  createShipmentStatus: (status: string) => Promise<void>;
  addItemToShipment: (sneakerId: number, statusId: number) => Promise<void>;
  removeItemFromShipment: (sneakerId: number) => Promise<void>;
  
  // Поиск
  setSearchQuery: (query: string) => void;
  
  // Пагинация
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  
  // Фильтры
  setSelectedArticleId: (articleId: string | null) => void;
  setSelectedModelId: (modelId: string | null) => void;
  setPriceFrom: (price: number | null) => void;
  setPriceTo: (price: number | null) => void;
  setDaysOnShipment: (days: number | null) => void;
  setSelectedShipmentStatusId: (statusId: number | null) => void;
  fetchAllArticles: () => Promise<void>;
  fetchAllModels: () => Promise<void>;
  applyFilters: () => Promise<void>;
  clearFilters: () => void;
  
  // Сброс состояния
  resetShipmentsState: () => void;
}

const initialState = {
  shipmentStatuses: [],
  shipmentsItems: [],
  
  isLoading: false,
  isInitialLoading: true,
  isLoadingStatuses: false,
  isLoadingArticles: false,
  isLoadingModels: false,
  
  searchQuery: '',
  
  currentPage: 1,
  itemsPerPage: 5,
  totalItems: 0,
  
  // Фильтры
  selectedArticleId: null,
  selectedModelId: null,
  priceFrom: null,
  priceTo: null,
  daysOnShipment: null,
  selectedShipmentStatusId: null,
  
  // Доступные значения для фильтров
  availableArticles: [],
  availableModels: [],
};

export const useShipmentsStore = create<ShipmentsState>()((set, get) => ({
  ...initialState,

  fetchShipmentStatuses: async () => {
    set({ isLoadingStatuses: true });
    try {
      const api = new Api();
      const statuses = await api.getShipmentStatuses();
      // Убеждаемся, что statuses это массив
      set({ shipmentStatuses: Array.isArray(statuses) ? statuses : [] });
    } catch (error) {
      console.error('Ошибка при загрузке статусов отгрузок:', error);
      set({ shipmentStatuses: [] }); // Устанавливаем пустой массив при ошибке
    } finally {
      set({ isLoadingStatuses: false });
    }
  },

  fetchShipmentsItems: async (params = {}) => {
    const state = get();
    // Используем isInitialLoading только при первой загрузке
    if (state.isInitialLoading) {
      set({ isLoading: true });
    } else {
      set({ isLoading: true });
    }
    
    try {
      const api = new Api();
      
      // Используем правильный метод getShipments для получения товаров в отгрузке
      const response = await api.getShipments(
        (params.page || get().currentPage - 1) * (params.limit || get().itemsPerPage),
        params.limit || get().itemsPerPage
      );
      
      // Убеждаемся, что данные это массивы
      const responseData = Array.isArray(response.data) ? response.data : [];
      
      // Группируем данные согласно новой структуре API
      const transformApiDataToGroupedItems = (brandGroups: any[]): GroupedShipmentItem[] => {
        const groupedMap = new Map();
        
        brandGroups.forEach((brandGroup, brandIndex) => {
          if (!brandGroup || !brandGroup.products || !Array.isArray(brandGroup.products)) {
            return;
          }

          const brandName = brandGroup.brand || 'Неизвестный бренд';
          
          brandGroup.products.forEach((product: any, productIndex: number) => {
            if (!product || !product.sneakers || !Array.isArray(product.sneakers)) {
              return;
            }
            
            const article = product.article || `${brandName}-${productIndex}`;
            
            // Обрабатываем каждый sneaker внутри продукта
            product.sneakers.forEach((sneakerData: any, sneakerIndex: number) => {
              if (!sneakerData || !sneakerData.sneaker) {
                return;
              }
              
              // Извлекаем данные из правильной структуры
              const sneaker = sneakerData.sneaker;
              let model = sneaker.model || 'Неизвестная модель';
              
              // Удаляем бренд из модели, если он там есть
              if (model.startsWith(brandName)) {
                model = model.substring(brandName.length).trim();
              }
              
              const key = `${brandName}_${model}_${article}`;
              
              if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                  brand: brandName,
                  model: model,
                  color: sneaker.color || 'Неизвестный цвет',
                  article: article,
                  imageUrl: sneakerData.photo_url || '', // Изображение на верхнем уровне
                  sizes: [],
                  priceRange: { min: Infinity, max: -Infinity }
                });
              }
              
              const group = groupedMap.get(key);
              
              // Извлекаем данные из правильных мест
              const price = sneaker.price || 0;
              const sizeValue = `${sneaker.us_size} US` || `${sneaker.eu_size} EU` || 'Неизвестный размер';
              
              group.sizes.push({
                id: sneaker.id || `${brandIndex}-${productIndex}-${sneakerIndex}`,
                size: sizeValue,
                price: price.toString(),
                purchasePrice: sneaker.purchase_price || 0,
                status: sneaker.status || 'Без статуса',
                shipmentDate: sneaker.shipment_set_data || null,
                inShipmentDays: sneaker.in_shipment_days,
                avgMarketPrice: sneakerData.prices?.avg_price?.toString() || '—',
                stockXPrice: sneakerData.prices?.stock_x?.toString() || '—',
                goatPrice: sneakerData.prices?.goat?.toString() || '—',
                poizonPrice: sneakerData.prices?.poizon?.toString() || '—'
              });
              
              const numPrice = parseInt(price) || 0;
              if (numPrice > 0) {
                if (numPrice < group.priceRange.min) group.priceRange.min = numPrice;
                if (numPrice > group.priceRange.max) group.priceRange.max = numPrice;
              }
            });
          });
        });
        
        // Исправляем priceRange для групп без цен
        Array.from(groupedMap.values()).forEach(group => {
          if (group.priceRange.min === Infinity) {
            group.priceRange.min = 0;
            group.priceRange.max = 0;
          }
        });
        
        return Array.from(groupedMap.values());
      };

      const groupedData = transformApiDataToGroupedItems(responseData);
      
      set({ 
        shipmentsItems: groupedData,
        totalItems: response.total_inventory_count || groupedData.length,
        isInitialLoading: false // После первой загрузки убираем флаг
      });
    } catch (error) {
      console.error('Ошибка при загрузке товаров отгрузки:', error);
      set({ shipmentsItems: [], isInitialLoading: false });
    } finally {
      set({ isLoading: false });
    }
  },


  createShipmentStatus: async (status: string) => {
    try {
      const api = new Api();
      const newStatus = await api.createShipmentStatus({ status });
      
      // Добавляем новый статус в список
      set(state => ({
        shipmentStatuses: [...state.shipmentStatuses, {
          id: newStatus.id,
          status: newStatus.status,
          vendor_id: 0 // Или получать из API ответа
        }]
      }));
    } catch (error) {
      console.error('Ошибка при создании статуса отгрузки:', error);
      throw error;
    }
  },

  addItemToShipment: async (sneakerId: number, statusId: number) => {
    try {
      const api = new Api();
      await api.createShipment({ sneaker_id: sneakerId, status_id: statusId });
      
      // Перезагружаем список товаров отгрузки
      await get().fetchShipmentsItems();
    } catch (error) {
      console.error('Ошибка при добавлении товара в отгрузку:', error);
      throw error;
    }
  },

  removeItemFromShipment: async (sneakerId: number) => {
    try {
      const api = new Api();
      await api.deleteShipment({ sneaker_id: sneakerId });
      
      // Перезагружаем список товаров отгрузки
      await get().fetchShipmentsItems();
    } catch (error) {
      console.error('Ошибка при удалении товара из отгрузки:', error);
      throw error;
    }
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },


  setCurrentPage: (page: number) => {
    set({ currentPage: page });
  },

  setItemsPerPage: (itemsPerPage: number) => {
    set({ itemsPerPage, currentPage: 1 });
  },

  // Методы для фильтрации
  setSelectedArticleId: (articleId: string | null) => {
    set({ selectedArticleId: articleId });
  },

  setSelectedModelId: (modelId: string | null) => {
    set({ selectedModelId: modelId });
  },

  setPriceFrom: (price: number | null) => {
    set({ priceFrom: price });
  },

  setPriceTo: (price: number | null) => {
    set({ priceTo: price });
  },

  setDaysOnShipment: (days: number | null) => {
    set({ daysOnShipment: days });
  },

  setSelectedShipmentStatusId: (statusId: number | null) => {
    set({ selectedShipmentStatusId: statusId });
  },

  fetchAllArticles: async () => {
    set({ isLoadingArticles: true });
    try {
      const api = new Api();
      const response = await api.getSneakerArticles();
      set({ availableArticles: response.articles });
    } catch (error) {
      console.error('Ошибка при загрузке артикулов:', error);
      set({ availableArticles: [] });
    } finally {
      set({ isLoadingArticles: false });
    }
  },

  fetchAllModels: async () => {
    set({ isLoadingModels: true });
    try {
      const api = new Api();
      const response = await api.getSneakerModels();
      set({ availableModels: response.models });
    } catch (error) {
      console.error('Ошибка при загрузке моделей:', error);
      set({ availableModels: [] });
    } finally {
      set({ isLoadingModels: false });
    }
  },

  applyFilters: async () => {
    const state = get();
    const offset = (state.currentPage - 1) * state.itemsPerPage;
    const limit = state.itemsPerPage;

    set({ isLoading: true });
    try {
      const api = new Api();
      const response = await api.getShipments(
        offset,
        limit,
        state.selectedArticleId || undefined,
        state.selectedModelId || undefined,
        state.priceFrom || undefined,
        state.priceTo || undefined,
        state.daysOnShipment || undefined,
        state.selectedShipmentStatusId || undefined
      );

      const responseData = Array.isArray(response.data) ? response.data : [];
      
      // Используем ту же функцию трансформации данных
      const transformApiDataToGroupedItems = (brandGroups: any[]): GroupedShipmentItem[] => {
        const groupedMap = new Map();
        
        brandGroups.forEach((brandGroup, brandIndex) => {
          if (!brandGroup || !brandGroup.products || !Array.isArray(brandGroup.products)) {
            return;
          }

          const brandName = brandGroup.brand || 'Неизвестный бренд';
          
          brandGroup.products.forEach((product: any, productIndex: number) => {
            if (!product || !product.sneakers || !Array.isArray(product.sneakers)) {
              return;
            }
            
            const article = product.article || `${brandName}-${productIndex}`;
            
            product.sneakers.forEach((sneakerData: any, sneakerIndex: number) => {
              if (!sneakerData || !sneakerData.sneaker) {
                return;
              }
              
              const sneaker = sneakerData.sneaker;
              let model = sneaker.model || 'Неизвестная модель';
              
              // Удаляем бренд из модели, если он там есть
              if (model.startsWith(brandName)) {
                model = model.substring(brandName.length).trim();
              }
              
              const key = `${brandName}_${model}_${article}`;
              
              if (!groupedMap.has(key)) {
                groupedMap.set(key, {
                  brand: brandName,
                  model: model,
                  color: sneaker.color || 'Неизвестный цвет',
                  article: article,
                  imageUrl: sneakerData.photo_url || '',
                  sizes: [],
                  priceRange: { min: Infinity, max: -Infinity }
                });
              }
              
              const group = groupedMap.get(key);
              const price = sneaker.price || 0;
              const sizeValue = `${sneaker.us_size} US` || `${sneaker.eu_size} EU` || 'Неизвестный размер';
              
              group.sizes.push({
                id: sneaker.id || `${brandIndex}-${productIndex}-${sneakerIndex}`,
                size: sizeValue,
                price: price.toString(),
                purchasePrice: sneaker.purchase_price || 0,
                status: sneaker.status || 'Без статуса',
                shipmentDate: sneaker.shipment_set_data || null,
                inShipmentDays: sneaker.in_shipment_days,
                avgMarketPrice: sneakerData.prices?.avg_price?.toString() || '—',
                stockXPrice: sneakerData.prices?.stock_x?.toString() || '—',
                goatPrice: sneakerData.prices?.goat?.toString() || '—',
                poizonPrice: sneakerData.prices?.poizon?.toString() || '—'
              });
              
              const numPrice = parseInt(price) || 0;
              if (numPrice > 0) {
                if (numPrice < group.priceRange.min) group.priceRange.min = numPrice;
                if (numPrice > group.priceRange.max) group.priceRange.max = numPrice;
              }
            });
          });
        });
        
        Array.from(groupedMap.values()).forEach(group => {
          if (group.priceRange.min === Infinity) {
            group.priceRange.min = 0;
            group.priceRange.max = 0;
          }
        });
        
        return Array.from(groupedMap.values());
      };

      const groupedData = transformApiDataToGroupedItems(responseData);
      
      set({ 
        shipmentsItems: groupedData,
        totalItems: response.total_inventory_count || groupedData.length
      });
    } catch (error) {
      console.error('Ошибка при применении фильтров:', error);
      set({ shipmentsItems: [] });
    } finally {
      set({ isLoading: false });
    }
  },

  clearFilters: () => {
    set({
      selectedArticleId: null,
      selectedModelId: null,
      priceFrom: null,
      priceTo: null,
      daysOnShipment: null,
      selectedShipmentStatusId: null,
      currentPage: 1,
    });
    get().fetchShipmentsItems();
  },

  resetShipmentsState: () => {
    set(initialState);
  },
}));
